import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CancelButton, DeleteButton, EditButton, SaveButton } from '@rd-web-markets/shared/dist/util/buttons';

const ChecklistTemplateRow = ({ row, onUpdate, onDelete }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    question: row.question || '',
    option_1: row.option_1 || '',
    option_1_requires_comments: !!row.option_1_requires_comments,
    option_2: row.option_2 || '',
    option_2_requires_comments: !!row.option_2_requires_comments,
  });

  useEffect(() => {
    setFormData({
      question: row.question || '',
      option_1: row.option_1 || '',
      option_1_requires_comments: !!row.option_1_requires_comments,
      option_2: row.option_2 || '',
      option_2_requires_comments: !!row.option_2_requires_comments,
    });
  }, [row]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const canSave =
    formData.question.trim() !== '' &&
    formData.option_1.trim() !== '' &&
    formData.option_2.trim() !== '';

  const handleSave = async () => {
    try {
      setSaving(true);
      await onUpdate({ id: row.id, data: formData });
      setEditing(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(t('confirm_delete'))) {
      await onDelete(row.id);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      question: row.question || '',
      option_1: row.option_1 || '',
      option_1_requires_comments: !!row.option_1_requires_comments,
      option_2: row.option_2 || '',
      option_2_requires_comments: !!row.option_2_requires_comments,
    });
  };

  return (
    <Card className="h-100 shadow-sm">
      <Card.Body>
        {!editing ? (
          <>
            <div className="mb-3">
              <strong>{t('question')}:</strong>
              <p className="mb-0 text-muted">{row.question}</p>
            </div>

            <Row className="mb-2">
              <Col md={6}>
                <strong>{t('option_1')}:</strong>
                <p className="mb-0 text-muted">{row.option_1}</p>
                {row.option_1_requires_comments && (
                  <small className="text-info">({t('requires_comments')})</small>
                )}
              </Col>
              <Col md={6}>
                <strong>{t('option_2')}:</strong>
                <p className="mb-0 text-muted">{row.option_2}</p>
                {row.option_2_requires_comments && (
                  <small className="text-info">({t('requires_comments')})</small>
                )}
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <EditButton onClick={() => setEditing(true)} />
              <DeleteButton size="sm" onClick={handleDelete} />
            </div>
          </>
        ) : (
          <>
            <Form.Group controlId={`question_${row.id}`} className="mb-3">
              <Form.Label>{t('question')}</Form.Label>
              <Form.Control
                type="text"
                name="question"
                value={formData.question}
                onChange={handleChange}
                placeholder={t('enter_question')}
              />
            </Form.Group>

            <Row className="mb-3">
              <Col md={5}>
                <Form.Group controlId={`option_1_${row.id}`}>
                  <Form.Label>{t('option_1')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="option_1"
                    value={formData.option_1}
                    onChange={handleChange}
                    placeholder={t('enter_option_1')}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Form.Group controlId={`option_1_requires_comments_${row.id}`}>
                  <Form.Check
                    type="checkbox"
                    label={t('requires_comments')}
                    name="option_1_requires_comments"
                    checked={formData.option_1_requires_comments}
                    onChange={handleCheckboxChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={5}>
                <Form.Group controlId={`option_2_${row.id}`}>
                  <Form.Label>{t('option_2')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="option_2"
                    value={formData.option_2}
                    onChange={handleChange}
                    placeholder={t('enter_option_2')}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Form.Group controlId={`option_2_requires_comments_${row.id}`}>
                  <Form.Check
                    type="checkbox"
                    label={t('requires_comments')}
                    name="option_2_requires_comments"
                    checked={formData.option_2_requires_comments}
                    onChange={handleCheckboxChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
              <SaveButton
                onClick={handleSave}
                disabled={saving || !canSave}
                text={saving ? t('saving') : t('save')}
              />
              <CancelButton
                onClick={handleCancel}
                disabled={saving}
              />
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ChecklistTemplateRow;